import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import useAuthHook from '../../lib/hooks/useAuthHook';

// Types for navigation items
interface NavItem {
  name: string;
  href: string;
  icon: string;
  roles?: string[];
  subItems?: { name: string; href: string; }[];
}

interface AdminLayoutProps {
  children: React.ReactNode;
  title?: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, title = 'Admin Dashboard' }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const location = useLocation();
  const { user, handleLogout } = useAuthHook();
  const mainRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname]);

  // Auto-expand 'Finance' if current path is under finance
  React.useEffect(() => {
    if (location.pathname.includes('/finance')) {
      setExpandedItems((prev) => {
        const next = new Set(prev);
        next.add('Finance');
        return next;
      });
    }
  }, [location.pathname]);

  const navigation: NavItem[] = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: 'chart-bar' },
    {
      name: 'Bookings',
      href: '#',
      icon: 'clipboard-document',
      subItems: [
        { name: 'Package Bookings', href: '/admin/bookings/packages' },
        { name: 'Group Trip Bookings', href: '/admin/bookings/group-trips' },
        { name: 'General Inquiries', href: '/admin/bookings/inquiries' },
        { name: 'Visa Applications', href: '/admin/bookings/visa-applications' },
        { name: 'Flight Bookings', href: '/admin/bookings/flights' }
      ]
    },
    {
      name: 'Finance',
      href: '#',
      icon: 'banknotes',
      roles: ['admin', 'finance'],
      subItems: [
        { name: 'Finance Overview', href: '/admin/finance' },
        { name: 'Invoices', href: '/admin/finance/invoices' },
        { name: 'E-Receipts', href: '/admin/finance/receipts' },
        { name: 'Clients (Receivables)', href: '/admin/finance/clients' },
        { name: 'Suppliers Directory', href: '/admin/finance/suppliers' },
        { name: 'Supplier Bills (Payables)', href: '/admin/finance/bills' },
        { name: 'Travel Vouchers', href: '/admin/finance/vouchers' },
        { name: 'Staff Registry', href: '/admin/finance/staff' },
        { name: 'Financial Reports', href: '/admin/finance/reports' },
        { name: 'Finance Settings', href: '/admin/finance/settings' }
      ]
    },
    {
      name: 'Destinations',
      href: '#',
      icon: 'globe',
      subItems: [
        { name: 'All Regions & Overview', href: '/admin/destinations' },
        { name: 'Countries & Guides', href: '/admin/destinations/countries' },
        { name: 'Travel Guide Categories', href: '/admin/travel-guides/categories' }
      ]
    },
    { name: 'Holiday Types', href: '/admin/holiday-types', icon: 'tag' },
    { name: 'Content Tags', href: '/admin/tags', icon: 'labels' },
    { name: 'Packages', href: '/admin/packages', icon: 'package' },
    { name: 'Group Trips', href: '/admin/group-trips', icon: 'users' },
    { name: 'Hotels', href: '/admin/hotels', icon: 'home' },
    { name: 'Hotel Types', href: '/admin/hotel-types', icon: 'building' },
    { name: 'Amenities', href: '/admin/amenities', icon: 'sparkles' },
    { name: 'Attractions', href: '/admin/attractions', icon: 'ticket' },
    { name: 'Activities', href: '/admin/activities', icon: 'ticket' },
    {
      name: 'Inclusions & Exclusions',
      href: '#',
      icon: 'list-check',
      subItems: [
        { name: 'Inclusions', href: '/admin/inclusions' },
        { name: 'Exclusions', href: '/admin/exclusions' }
      ]
    },
    { name: 'Blog', href: '/admin/blog', icon: 'newspaper' },
    { name: 'Content', href: '/admin/content', icon: 'document-text' },
    { name: 'Partners', href: '/admin/partners', icon: 'handshake' },
    { name: 'Users', href: '/admin/users', icon: 'user-group' },
    { name: 'Newsletter', href: '/admin/newsletter', icon: 'mail' },
    { name: 'Email Logs', href: '/admin/logs/email', icon: 'document-text' },
    { name: 'Settings', href: '/admin/settings', icon: 'cog' },
  ];

  const isSuperuser = !!user?.is_superuser;
  const userRoleNames = user?.roles?.map((r) => r.name) || [];

  const visibleNavigation = navigation.filter((item) => {
    if (isSuperuser) return true;
    if (item.roles && item.roles.length > 0) {
      return item.roles.some((r) => userRoleNames.includes(r));
    }
    return false;
  });

  const homePath = isSuperuser ? '/admin/dashboard' : '/admin/finance';
  const roleTitle = isSuperuser ? 'Admin' : userRoleNames.includes('finance') ? 'Finance' : 'Portal';

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const toggleExpanded = (itemName: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemName)) {
      newExpanded.delete(itemName);
    } else {
      newExpanded.add(itemName);
    }
    setExpandedItems(newExpanded);
  };

  // Render icon based on name
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case 'chart-bar':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'globe':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'tag':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        );
      case 'package':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        );
      case 'users':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      case 'photograph':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'newspaper':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
        );
      case 'user-group':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'cog':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      case 'home':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        );
      case 'ticket':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
          </svg>
        );
      case 'mail':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'building':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'list-check':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      case 'clipboard-document':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'document-text':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'handshake':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'labels':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4zM17 3h1a2 2 0 012 2v1" />
          </svg>
        );
      case 'banknotes':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        );
    }
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100 print:h-auto print:overflow-visible print:bg-white">
      <Helmet>
        <title>{title} | AllBounds Admin</title>
      </Helmet>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden no-print print:hidden">
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity"
            onClick={() => setSidebarOpen(false)}
          ></div>

          <div className="fixed inset-0 flex z-40">
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-charcoal">
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  type="button"
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="sr-only">Close sidebar</span>
                  <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Mobile sidebar content */}
              <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <div className="flex-shrink-0 flex items-center px-4">
                  <Link to={homePath} className="text-white text-xl font-bold">
                    AllBounds {roleTitle}
                  </Link>
                </div>
                <nav className="mt-5 px-2 space-y-1">
                  {visibleNavigation.map((item) => (
                    <div key={item.name}>
                      {item.subItems ? (
                        <button
                          onClick={() => toggleExpanded(item.name)}
                          className={`group flex items-center w-full px-2 py-2 text-base font-medium rounded-md ${isActive(item.href)
                            ? 'bg-teal text-white'
                            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                            }`}
                        >
                          <div className="mr-4 flex-shrink-0 h-6 w-6">
                            {renderIcon(item.icon)}
                          </div>
                          {item.name}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`ml-auto h-5 w-5 transition-transform ${expandedItems.has(item.name) ? 'rotate-180' : ''}`}
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      ) : (
                        <Link
                          to={item.href}
                          className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActive(item.href)
                            ? 'bg-teal text-white'
                            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                            }`}
                        >
                          <div className="mr-4 flex-shrink-0 h-6 w-6">
                            {renderIcon(item.icon)}
                          </div>
                          {item.name}
                        </Link>
                      )}

                      {item.subItems && expandedItems.has(item.name) && (
                        <div className="ml-8 mt-1 space-y-1">
                          {item.subItems.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className={`group flex items-center px-2 py-1 text-sm font-medium rounded-md ${location.pathname === subItem.href
                                ? 'bg-gray-700 text-white'
                                : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                                }`}
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </nav>
              </div>

              {/* Mobile user profile */}
              <div className="flex-shrink-0 flex border-t border-gray-700 p-4">
                <div className="flex-shrink-0 group block">
                  <div className="flex items-center">
                    <div>
                      <div className="h-10 w-10 rounded-full bg-gray-700 flex items-center justify-center text-white">
                        {user?.first_name?.[0]}{user?.last_name?.[0]}
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-base font-medium text-white">
                        {user?.first_name} {user?.last_name}
                      </p>
                      <button
                        onClick={handleLogout}
                        className="text-sm font-medium text-gray-300 hover:text-white"
                      >
                        Sign out
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0 no-print print:hidden">
        <div className="flex flex-col w-56">
          <div className="flex flex-col h-0 flex-1 bg-charcoal">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <Link to={homePath} className="text-white text-xl font-bold">
                  AllBounds {roleTitle}
                </Link>
              </div>
              <nav className="mt-5 flex-1 px-2 space-y-1">
                {visibleNavigation.map((item) => (
                  <div key={item.name}>
                    {item.subItems ? (
                      <button
                        onClick={() => toggleExpanded(item.name)}
                        className={`group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md ${isActive(item.href)
                          ? 'bg-teal text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                          }`}
                      >
                        <div className="mr-3 flex-shrink-0 h-6 w-6">
                          {renderIcon(item.icon)}
                        </div>
                        {item.name}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`ml-auto h-4 w-4 transition-transform ${expandedItems.has(item.name) ? 'rotate-180' : ''}`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    ) : (
                      <Link
                        to={item.href}
                        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive(item.href)
                          ? 'bg-teal text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                          }`}
                      >
                        <div className="mr-3 flex-shrink-0 h-6 w-6">
                          {renderIcon(item.icon)}
                        </div>
                        {item.name}
                      </Link>
                    )}

                    {item.subItems && expandedItems.has(item.name) && (
                      <div className="ml-7 mt-1 space-y-1">
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className={`group flex items-center px-2 py-1 text-xs font-medium rounded-md ${location.pathname === subItem.href
                              ? 'bg-gray-700 text-white'
                              : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                              }`}
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>
            <div className="flex-shrink-0 flex border-t border-gray-700 p-4">
              <div className="flex-shrink-0 w-full group block">
                <div className="flex items-center">
                  <div>
                    <div className="h-9 w-9 rounded-full bg-gray-700 flex items-center justify-center text-white">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-white">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <button
                      onClick={handleLogout}
                      className="text-xs font-medium text-gray-300 hover:text-white"
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden print:w-full print:overflow-visible">
        {/* Top header */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow no-print print:hidden">
          <button
            type="button"
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-teal md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex items-center">
              <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
            </div>
            <div className="ml-4 flex items-center md:ml-6">
              {/* Visit site button */}
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal"
              >
                <span className="sr-only">Visit site</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main ref={mainRef} className="flex-1 relative overflow-y-auto focus:outline-none print:overflow-visible print:p-0">
          <div className="py-4 print:py-0">
            <div className="max-w-none px-4 sm:px-6 lg:px-8 print:p-0 print:m-0">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
